import {Fragment, memo} from "react";
import {Autocomplete, Button, FormControl, Grid, InputLabel, ListSubheader, MenuItem, Select, TextField} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AudienceRangeInput from "./AudienceRangeInput";
import {appendTypedValue, describeParameter, filterCohortOptions, multiValue, requirementHasError} from "./audienceEditorModel";

const groupBy = (option) => option.isMain ? 'POPULAR' : 'OTHER';
const renderGroup = (params) => (
    <Fragment key={params.key}>
        <ListSubheader>{params.group}</ListSubheader>
        {params.children}
    </Fragment>
);
const parameterLabel = (option) => option?.parameterName ?? '';
const sameParameter = (option, value) => option?.parameterName === value?.parameterName;
const popperProps = {popper: {style: {width: 'fit-content'}}};

/**
 * Одна строка условия аудитории: параметр, оператор и значение (вид контрола зависит от параметра).
 * memo: при вводе в одну строку остальные не перерисовываются (обработчики стабильны, параметры иммутабельны).
 */
function AudienceParameterRow({
                                  parameterSet, paramIndex, audienceIndex, canDelete, params, paramsIndex, variant,
                                  abbreviations, rangeError, actions,
                              }) {
    const row = describeParameter(parameterSet, paramsIndex, variant);
    const labelId = `operator-label-${paramIndex}-${audienceIndex}`;

    const changeValue = (value) => actions.valueChange(value, paramIndex, audienceIndex);
    const changeOffset = (field) => (e) => actions.offsetChange(e, field, paramIndex, audienceIndex);
    const optionLabel = (option) => option + (abbreviations[option] ? ' (' + abbreviations[option] + ')' : '');

    const renderMultiValue = () => {
        const options = variant.filterCohortsByAbTest && row.name === 'ab_test_cohort'
            ? filterCohortOptions(row.valueOptions, parameterSet.paramValue)
            : row.valueOptions;
        return <Autocomplete
            fullWidth
            freeSolo={row.valueFreeSolo}
            multiple={row.valueMultiple}
            limitTags={1}
            getOptionLabel={optionLabel}
            componentsProps={popperProps}
            value={multiValue(parameterSet.paramValue, row.valueMultiple)}
            options={options}
            onChange={(event, value) => changeValue(value)}
            onBlur={(event) => {
                if (!row.valueMultiple) return;
                const next = appendTypedValue(parameterSet.paramValue, event.target.value);
                if (next) changeValue(next);
            }}
            renderInput={(inputParams) => (
                <TextField
                    error={!!parameterSet.valueError}
                    {...inputParams}
                    id="value"
                    name="value"
                    label="Value (press enter to split)"
                />
            )}
        />;
    };

    const renderOffset = () => {
        const [offerWidth, operatorWidth, offsetWidth] = variant.offsetWidths;
        return <Grid container>
            <Grid item sm={offerWidth} pr={0.5}>
                <TextField
                    fullWidth
                    error={!!parameterSet.offerValueError}
                    id="offerId"
                    name="offerId"
                    label="Offer id"
                    value={parameterSet.offerId ?? ''}
                    onChange={changeOffset('offerId')}
                />
            </Grid>
            <Grid item sm={operatorWidth} pr={0.5}>
                <FormControl fullWidth error={!!parameterSet.operatorValueError}>
                    <InputLabel id={labelId}>Operator</InputLabel>
                    <Select
                        labelId={labelId}
                        id="operator"
                        name="operator"
                        label="Operator"
                        value={['GREATER_THAN', 'LESS_THAN'].includes(parameterSet.operator) ? parameterSet.operator : ''}
                        onChange={changeOffset('operator')}
                    >
                        <MenuItem value=""></MenuItem>
                        <MenuItem value="GREATER_THAN">{">"}</MenuItem>
                        <MenuItem value="LESS_THAN">{"<"}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item sm={offsetWidth} pl={0.5}>
                <TextField
                    fullWidth
                    error={!!parameterSet.offsetValueError}
                    id="offsetValue"
                    name="offsetValue"
                    label="Offset value (for example PT24H)"
                    value={parameterSet.offset ?? ''}
                    onChange={changeOffset('offset')}
                />
            </Grid>
        </Grid>;
    };

    const renderValue = () => {
        if (row.valueKind === 'multi') return renderMultiValue();
        if (row.valueKind === 'offset') return renderOffset();
        return <TextField
            fullWidth
            error={!!parameterSet.valueError}
            id="value"
            name="value"
            label="Value"
            value={parameterSet.paramValue ?? ''}
            onChange={(e) => actions.manualInput(e, paramIndex, audienceIndex)}
        />;
    };

    return (
        <>
            {params && <Grid item xs={3}>
                <Autocomplete
                    disableClearable
                    options={params}
                    groupBy={groupBy}
                    renderGroup={renderGroup}
                    getOptionLabel={parameterLabel}
                    isOptionEqualToValue={sameParameter}
                    value={row.name ? parameterSet.parameterData : null}
                    onChange={(event, value) => actions.parameterChange(params, value.parameterName, paramIndex, audienceIndex)}
                    renderInput={(inputParams) => (
                        <TextField
                            {...inputParams}
                            id="parameterData"
                            name="parameterData"
                            label="Requirement"
                            error={requirementHasError(parameterSet)}
                        />
                    )}
                />
            </Grid>}

            {row.showOperatorColumn &&
                <Grid item xs={row.operatorColumnWidth}>
                    {row.valueKind === 'range'
                        ? <AudienceRangeInput
                            parameterSet={parameterSet}
                            error={rangeError}
                            onChange={(field, value) => actions.rangeChange(field, value, paramIndex, audienceIndex)}
                        />
                        : <Autocomplete
                            disableClearable
                            freeSolo={row.operatorFreeSolo}
                            options={row.operatorOptions}
                            value={parameterSet.operator || ''}
                            onChange={(event, value) => actions.operatorChange(value, paramIndex, audienceIndex)}
                            renderInput={(inputParams) => (
                                <TextField
                                    {...inputParams}
                                    id="operator"
                                    name="operator"
                                    label="Operator"
                                    error={!!parameterSet.operatorError}
                                />
                            )}
                        />}
                </Grid>}

            {row.valueKind !== 'range' &&
                <Grid item xs={row.valueColumnWidth}>
                    {renderValue()}
                </Grid>}

            <Grid item xs={1} display={'flex'} justifyContent={'start'}>
                {canDelete &&
                    <Button
                        sx={{minWidth: '100%'}}
                        variant={'outlined'}
                        color={'error'}
                        aria-label="Delete condition"
                        onClick={() => actions.deleteParameter(audienceIndex, paramIndex)}
                    >
                        <DeleteIcon/>
                    </Button>}
            </Grid>
        </>
    );
}

export default memo(AudienceParameterRow);
